/**
 * In-house toast store — the single, app-wide notification API.
 *
 * Framework-agnostic pub/sub singleton so any module can raise a toast without
 * importing React. `<Toaster />` (mounted once in App.jsx) subscribes and renders.
 * This replaces `react-toastify`; the call surface (`toast.success/error/info/warn`)
 * is kept identical so consumers migrate with an import swap only.
 */

export type ToastType = "success" | "error" | "info" | "warn";

export interface ToastOptions {
  /**
   * Milliseconds until auto-dismiss. Pass `false` to disable auto-dismiss.
   * When omitted, the container's default (8000ms) applies.
   */
  autoClose?: number | false;
}

export interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  autoClose?: number | false;
}

type Listener = (toasts: ToastItem[]) => void;

let counter = 0;
let items: ToastItem[] = [];
const listeners = new Set<Listener>();

function emit(): void {
  const snapshot = items.slice();
  listeners.forEach((listener) => listener(snapshot));
}

function push(type: ToastType, message: string, opts?: ToastOptions): number {
  const id = ++counter;
  items = [...items, { id, type, message, autoClose: opts?.autoClose }];
  emit();
  return id;
}

/** Subscribe to store changes. Immediately invoked with the current list. Returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(items.slice());
  return () => {
    listeners.delete(listener);
  };
}

/** Remove a toast by id (used by the container for manual/auto dismissal). */
export function removeToast(id: number): void {
  items = items.filter((toastItem) => toastItem.id !== id);
  emit();
}

/** Remove every active toast. */
export function clearToasts(): void {
  items = [];
  emit();
}

export const toast = {
  success: (message: string, opts?: ToastOptions): number => push("success", message, opts),
  error: (message: string, opts?: ToastOptions): number => push("error", message, opts),
  info: (message: string, opts?: ToastOptions): number => push("info", message, opts),
  warn: (message: string, opts?: ToastOptions): number => push("warn", message, opts),
};
